import { memo } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Wallet } from "@zenith/assets/icons/components";
import {
  ACCOUNT_NUMBER_LABEL,
  AVAILABLE_BALANCE_LABEL,
  AVAILABLE_LIMIT_LABEL,
} from "@zenith/data/constants";
import { formatCurrency } from "@zenith/utils";

/**
 * Props for Wallet Component.
 * @interface
 * @name WalletComponentProps
 * @kind interface
 */
interface WalletComponentProps {
  accountNumber?: string;
  availableBalance?: number;
  availableLimit?: number;
  isInfoLoaded: () => boolean;
  /**
   * Optional title to replace the default "Available Balance" label.
   * @property
   * @name balanceTitle
   * @kind property
   * @memberof WalletComponentProps
   * @type {string}
   */
  balanceTitle?: string;
  /**
   * Optional title to replace the default "Available Limit" label.
   * @property
   * @name limitTitle
   * @kind property
   * @memberof WalletComponentProps
   * @type {string}
   */
  limitTitle?: string;
  /**
   * Optional remaining limit to display under the progress bar.
   * @property
   * @name remainingLimit
   * @kind property
   * @memberof WalletComponentProps
   * @type {number}
   */
  remainingLimit?: number;
  isBalanceVisible?: boolean;
  toggleBalanceVisibility?: () => void;
}

/**
 * Memoized Wallet Component.
 * @constant
 * @name WalletComponent
 * @kind variable
 * @type {React.NamedExoticComponent<WalletComponentProps>}
 */
const WalletComponent: React.NamedExoticComponent<WalletComponentProps> = memo(
  function WalletComponent(props: WalletComponentProps): JSX.Element {
    const {
      accountNumber,
      availableBalance,
      availableLimit,
      isInfoLoaded,
      balanceTitle = AVAILABLE_BALANCE_LABEL,
      limitTitle = AVAILABLE_LIMIT_LABEL,
      remainingLimit,
      isBalanceVisible = true,
      toggleBalanceVisibility,
    } = props;

    const progress =
      remainingLimit && availableLimit
        ? (remainingLimit / availableLimit) * 100
        : 0;

    return (
      <div className="inline-flex w-full flex-col items-end justify-start gap-4 rounded-lg bg-stone-50 px-4 py-6 text-neutral-600">
        {isInfoLoaded() ? (
          <>
            <div className=" flex w-full flex-row items-start">
              <div className="inline-flex  h-12 w-12 items-center justify-center rounded-lg bg-rose-300 bg-opacity-50 p-2">
                <Wallet />
              </div>

              <div className="ml-4 flex w-full flex-col gap-2">
                <div className="block w-full flex-row justify-between xs:flex">
                  <div className="flex flex-col">
                    <span>{ACCOUNT_NUMBER_LABEL}</span>
                    <span>{accountNumber}</span>
                  </div>
                  <div className="mt-2 flex flex-col items-start md:mt-0 md:items-end">
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-neutral-600">
                        {balanceTitle}
                      </span>
                      {toggleBalanceVisibility && (
                        <IconButton
                          onClick={toggleBalanceVisibility}
                          size="small"
                          className="p-0 text-neutral-600"
                        >
                          {isBalanceVisible ? (
                            <Visibility fontSize="small" />
                          ) : (
                            <VisibilityOff fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </div>
                    <span>
                      {isBalanceVisible
                        ? formatCurrency(availableBalance)
                        : "••••••••"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-full items-center gap-1">
              <span className="text-sm text-neutral-600">{limitTitle}:</span>
              <span>{formatCurrency(availableLimit)}</span>
            </div>
            {remainingLimit !== undefined && (
              <>
                <div className="h-2 w-full rounded-full bg-primary-300">
                  <div
                    className="h-full rounded-full bg-[#F3B8B380]"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="w-full text-right">
                  <span className="text-sm text-neutral-600">
                    Remaining: {formatCurrency(remainingLimit)}
                  </span>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="flex w-full justify-center text-center">
            <CircularProgress className={"text-[#F3B8B3]"} size={"4rem"} />
          </div>
        )}
      </div>
    );
  }
);

export default WalletComponent;
